import { ACFFormElement, FormState } from "./Form";
import dynamic from "next/dynamic";
import { FormElement } from "./createFormStructure";
/* jscpd:ignore-start */
import {
  FormErrorState,
  InputV2,
  TextAreaV2,
  SelectV2,
  FieldWrapper,
  LabelV2,
  CheckboxV2,
  RadioV2,
} from "./Inputs/Inputs";
import { FormData } from "@/getData/getIndexedFormsData";
import React from "react";

const FriendlyCaptcha = dynamic(
  () => import("../../components/FriendlyCaptcha/FriendlyCaptcha"),
  { ssr: false },
);
export const genFieldName = (fieldLabel: string) => {
  return fieldLabel.replace(/[ äüö.;]/g, "").toLocaleLowerCase();
};

const inputWidthToColumns = (width: ACFFormElement["input_width"]) => {
  switch (width) {
    case "full":
      return "is-12";
    case "half":
      return "is-6";
    case "one-third":
      return "is-4";
    case "two-third":
      return "is-8";
  }
};

interface RenderFieldsProps {
  fields: FormElement[];
  errors: FormErrorState;
  friendlyCaptchaApplicationKey: string | undefined;
  formValues: Record<string, string | boolean | number | string[]>;
  handleCaptchaComplete: (solution: string) => void;
  formState: FormState;
  formData: FormData;
  setFormValues: (
    values: Record<string, string | boolean | number | string[]>,
  ) => void;
  requiredLabel: string;
}

export const renderFields = ({
  fields,
  setFormValues,
  errors,
  friendlyCaptchaApplicationKey,
  formValues,
  handleCaptchaComplete,
  formState,
  formData,
  requiredLabel,
}: RenderFieldsProps) => {
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement & HTMLTextAreaElement>,
  ) => {
    if (e.target.type === "checkbox") {
      setFormValues({ ...formValues, [e.target.name]: e.target.checked });
    } else if (e.target.type === "number") {
      setFormValues({ ...formValues, [e.target.name]: e.target.valueAsNumber });
    } else if (e.target.type === "radio") {
      if (formValues[e.target.name] === e.target.value) {
        const { [e.target.name]: _, ...updatedFormValues } = formValues;
        setFormValues(updatedFormValues);
      } else {
        setFormValues({ ...formValues, [e.target.name]: e.target.value });
      }
    } else {
      setFormValues({ ...formValues, [e.target.name]: e.target.value });
    }
  };

  const elements: JSX.Element[] = [];
  for (const field of fields) {
    const elementKey =
      field.name ??
      (field.type === "fieldset" || field.type === "column"
        ? `${field.type}:${field.label ?? ""}`
        : `${field.type}:${field.label ?? ""}:${field.placeholder ?? ""}`);
    const className = `column ${inputWidthToColumns(field.inputWidth)} ${
      field.wrapperClassName
    }`;
    let Tag: JSX.Element = <></>;

    const props = {
      placeholder: field.placeholder,
      label: field.label,
      name: field.name,
      required: field.isRequired,
      className,
      onChange: handleChange,
      errors,
      "aria-label": field.ariaLabel,
      disabled: formState === "loading",
      required_label: requiredLabel,
    };
    const value = formValues[props.name];
    const nonBooleanValue =
      typeof value !== "boolean" ? (value ?? "") : undefined;
    const booleanValue =
      typeof value === "boolean" ? (value ?? false) : undefined;

    if (field.renderCondition) {
      if (!field.renderCondition(formValues)) {
        continue;
      }
    }

    switch (field.type) {
      case "column":
        Tag = (
          <div className="column is-12-tablet is-6-desktop">
            <div className="columns is-multiline">
              {renderFields({
                fields: field.children || [],
                setFormValues,
                errors,
                friendlyCaptchaApplicationKey,
                formValues,
                handleCaptchaComplete,
                formState,
                formData,
                requiredLabel,
              })}
            </div>
          </div>
        );
        break;
      case "fieldset":
        Tag = (
          <fieldset
            className={`column mb-6 ${
              /*field.inputWidth ? inputWidthToColumns(field.inputWidth) : "is-12"*/ "is-12"
            }`}
          >
            {field.label && <legend className={"legend"}>{field.label}</legend>}
            <div className="columns is-multiline">
              {renderFields({
                fields: field.children || [],
                setFormValues,
                errors,
                friendlyCaptchaApplicationKey,
                formValues,
                handleCaptchaComplete,
                formState,
                formData,
                requiredLabel,
              })}
            </div>
          </fieldset>
        );
        break;
      case "infotext":
        if (field.infoTextOptions !== "send-only") {
          Tag = (
            <div
              className={className}
              dangerouslySetInnerHTML={{ __html: field.infoText }}
            />
          );
        }
        break;
      case "text":
      case "email":
      case "tel":
      case "url":
        Tag = (
          <InputV2 inputType={field.type} {...props} value={nonBooleanValue} />
        );
        break;
      case "datepicker":
        Tag = (
          <InputV2
            inputType={"date"}
            {...props}
            min={field.min}
            max={field.max}
            value={nonBooleanValue}
          />
        );
        break;
      case "number":
        Tag = (
          <InputV2
            inputType={field.type}
            {...props}
            min={field.min}
            max={field.max}
            step={field.step}
            value={nonBooleanValue}
          />
        );
        break;
      case "textarea":
        Tag = <TextAreaV2 {...props} rows={field.rows} />;
        break;
      case "dropwdown":
        if (!field.multiSelect) {
          const { onChange: _, ...selectProps } = props;
          Tag = (
            <SelectV2
              items={field.items}
              setSelected={(value) => {
                setFormValues({ ...formValues, [props.name]: value });
              }}
              selected={formValues[props.name] as string}
              {...selectProps}
            />
          );
        } else {
          Tag = (
            <FieldWrapper
              errors={errors}
              name={props.name}
              className={`${className}`}
            >
              <LabelV2
                Tag="p"
                label={field.label}
                required={field.isRequired}
                required_label={requiredLabel}
              />
              <div
                className="columns is-multiline"
                style={{ "--bulma-column-gap": "4px" } as React.CSSProperties}
              >
                {field.items.map((item, index) => {
                  return (
                    <CheckboxV2
                      className="column is-6-tablet"
                      inputType="checkbox"
                      key={`${props.name}-${item.value ?? item.label ?? index}`}
                      label_checkbox={item.label}
                      onChange={(e) => {
                        const multiSelectValues = (formValues[props.name] ||
                          []) as string[];
                        if (e.target.checked) {
                          setFormValues({
                            ...formValues,
                            [props.name]: [
                              ...multiSelectValues,
                              index.toString(),
                            ],
                          });
                        } else {
                          setFormValues({
                            ...formValues,
                            [props.name]: multiSelectValues.filter(
                              (x) => x !== index.toString(),
                            ),
                          });
                        }
                      }}
                    />
                  );
                })}
              </div>
            </FieldWrapper>
          );
        }

        break;
      case "checkbox":
        Tag = (
          <CheckboxV2
            inputType="checkbox"
            label_checkbox={field.labelCheckbox}
            {...props}
            checked={booleanValue}
          />
        );
        break;
      case "radiobutton":
        Tag = (
          <RadioV2
            options={field.options.map((o, key) => ({
              ...o,
              checked: key === Number(formValues[props.name]),
            }))}
            {...props}
          />
        );
        break;
      case "captcha":
        console.error("Rendering captcha field", field);
        if (friendlyCaptchaApplicationKey) {
          const hasError = errors && props.name && errors[props.name];
          Tag = (
            <div
              data-field-name={props.name}
              className={`${hasError ? "has-error" : ""} ${props.className}`}
            >
              <FriendlyCaptcha
                onComplete={handleCaptchaComplete}
                sitekey={friendlyCaptchaApplicationKey}
              />
              {hasError && (
                <p className={`error`}>{hasError && errors[props.name!]}</p>
              )}
            </div>
          );
        }
    }
    elements.push(<React.Fragment key={elementKey}>{Tag}</React.Fragment>);
  }
  return elements;
};
/* jscpd:ignore-end */
